/**
 * Component Registry
 * Maps widget keys to React components for dynamic rendering
 *
 * Admin creates dashboard → saves widget keys + positions to BE
 * FE fetches config → looks up components here → renders
 */

import React, { ComponentType } from 'react';

// ===== CONTAINER WIDGETS =====
import { CardContainer } from '@/modules/shared/CustomDashboard/widgets/CardContainer';
import { GridContainer } from '@/modules/shared/CustomDashboard/widgets/GridContainer';

// ===== OPERATOR PROFICIENCY WIDGETS =====
import { FleetAbuseChart } from '@/modules/AssetsPreviewPage/OperatorProficiency/components/FleetAbuseChart';
import { FleetMostFrequentCard } from '@/modules/AssetsPreviewPage/OperatorProficiency/components/FleetMostFrequentCard';

// ===== ASSETS PREVIEW WIDGETS =====
import { BusinessInsights } from '@/components/pages/AssetsPreviewPage/BusinessInsights/BusinessInsights';
import { AssetsMaintenance } from '@/components/pages/AssetsPreviewPage/Maintenance';

// Dynamic import to break circular dependency
const AssetsOverview = React.lazy(() => 
  import('@/components/pages/AssetsPreviewPage/AssetsOverview').then(module => ({ default: module.AssetsOverview }))
);

// ===== SITES PAGE WIDGETS =====
import { Sites } from '@/components/pages/SitesPage/SitesContent/Sites';

// ===== COMMON WIDGETS =====
import { BaseTable } from '@/components/uiKit/Tables/BaseTable';

// ===== CHARTS =====
// Add chart components here as needed

/**
 * Widget metadata for the picker UI
 */
export interface WidgetMeta {
  id: string;
  name: string;
  description?: string;
  category: string;
  defaultSize: { w: number; h: number; minW?: number; minH?: number };
  component: ComponentType<any>;
  /** Is this a container widget that can hold other widgets? */
  isContainer?: boolean;
}

/**
 * All available widgets that can be added to dashboards
 */
export const WIDGET_REGISTRY: Record<string, WidgetMeta> = {
  // ===== CONTAINERS =====
  'card-container': {
    id: 'card-container',
    name: 'Card Container',
    description: 'A card wrapper that can hold other widgets',
    category: 'containers',
    defaultSize: { w: 6, h: 4, minW: 2, minH: 2 },
    component: CardContainer,
    isContainer: true,
  },
  'grid-container': {
    id: 'grid-container',
    name: 'Grid Container',
    description: 'A card with grid layout for multiple widgets',
    category: 'containers',
    defaultSize: { w: 12, h: 6, minW: 4, minH: 3 },
    component: GridContainer,
    isContainer: true,
  },

  // ===== WIDGETS =====
  // Operator Proficiency
  'fleet-abuse-chart': {
    id: 'fleet-abuse-chart',
    name: 'Fleet Abuse Chart',
    description: 'Shows fleet abuse statistics',
    category: 'charts',
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3 },
    component: FleetAbuseChart,
  },
  'fleet-most-frequent': {
    id: 'fleet-most-frequent',
    name: 'Most Frequent Issues',
    description: 'Shows most frequent fleet issues',
    category: 'cards',
    defaultSize: { w: 4, h: 6, minW: 3, minH: 4 },
    component: FleetMostFrequentCard,
  },

  // Assets
  'assets-overview': {
    id: 'assets-overview',
    name: 'Assets Overview',
    description: 'Overview of all assets',
    category: 'tables',
    defaultSize: { w: 12, h: 8, minW: 6, minH: 4 },
    component: AssetsOverview,
  },
  'business-insights': {
    id: 'business-insights',
    name: 'Business Insights',
    description: 'Business metrics and KPIs',
    category: 'charts',
    defaultSize: { w: 12, h: 6, minW: 6, minH: 4 },
    component: BusinessInsights,
  },
  'assets-maintenance': {
    id: 'assets-maintenance',
    name: 'Maintenance Data',
    description: 'Asset maintenance information',
    category: 'tables',
    defaultSize: { w: 12, h: 8, minW: 6, minH: 4 },
    component: AssetsMaintenance,
  },

  // Sites
  'sites-list': {
    id: 'sites-list',
    name: 'Sites List',
    description: 'List of all sites',
    category: 'tables',
    defaultSize: { w: 12, h: 8, minW: 6, minH: 4 },
    component: Sites,
  },

  // Generic
  'data-table': {
    id: 'data-table',
    name: 'Data Table',
    description: 'Generic data table',
    category: 'tables',
    defaultSize: { w: 12, h: 6, minW: 4, minH: 3 },
    component: BaseTable,
  },
};

/**
 * Get widget component by ID
 */
export function getWidgetComponent(widgetId: string): ComponentType<any> | undefined {
  return WIDGET_REGISTRY[widgetId]?.component;
}

/**
 * Get widget metadata by ID
 */
export function getWidgetMeta(widgetId: string): WidgetMeta | undefined {
  return WIDGET_REGISTRY[widgetId];
}

/**
 * Get all widgets in a category
 */
export function getWidgetsByCategory(category: string): WidgetMeta[] {
  return Object.values(WIDGET_REGISTRY).filter((w) => w.category === category);
}

/**
 * Get all available categories
 */
export function getCategories(): string[] {
  return [...new Set(Object.values(WIDGET_REGISTRY).map((w) => w.category))];
}

/**
 * Get all widgets as array
 */
export function getAllWidgets(): WidgetMeta[] {
  return Object.values(WIDGET_REGISTRY);
}
